import { type IncidentDto, type IncidentItemDto, IncidentSeverity } from '@api/incidents';
import { utils } from '@core';
import { FormatNumberPipe } from '@core';
import { type DeviceChart } from '@devices';
import * as Highcharts from 'highcharts';

import { formatEnergyValue } from '../../core/src/lib/computations/energy';
import { type WidgetGauge } from '../../dashboards/src/lib/models';


const chartSpacing = [16, 24, 8, 24];

const compactResponsiveRules: Highcharts.ResponsiveRulesOptions[] = [
  {
    chartOptions: {
      chart: {
        spacing: [8, 8, 4, 8],
      },
      legend: {
        enabled: false,
      },
      yAxis: {
        title: {
          text: undefined,
        },
      },
    },
    condition: {
      maxHeight: 200,
    },
  },
];

const exportingConfig = {
  credits: {
    enabled: false,
  },
  exporting: {
    buttons: {
      contextButton: {
        menuItems: ['viewFullscreen', 'separator', 'downloadPNG', 'downloadJPEG', 'downloadPDF', 'downloadSVG'],
        titleKey: '',
        y: -8,
      },
    },
    fallbackToExportServer: false,
  },
};

interface CreateChartParams {
  chart: DeviceChart;
  noTitle: boolean;
  paused?: DeviceChart | null;
  showDots: boolean;
}

export const createArbitraryValueChart = ({ chart, paused, showDots = true, noTitle = false }: CreateChartParams): Highcharts.Options => {
  const isKWh = chart.measurementUnit?.displayShort?.toLowerCase() === 'kwh';
  const shouldConvertToWh = isKWh && chart.timeseries.some(item => {
    const val = Number(item.value);

    return !isNaN(val) && val < 1;
  });

  const displayUnitShort = shouldConvertToWh ? 'Wh' : (chart.measurementUnit?.displayShort || '');
  const displayUnitLong = shouldConvertToWh ? 'Watt Hour' : (chart.measurementUnit?.display || '');

  const isPausedAt = (timestamp: number): boolean => {
    if (!paused || !paused.timeseries.length) return false;

    const pausedSeries = [...paused.timeseries].sort((a, b) =>
      Date.parse(a.timeStamp) - Date.parse(b.timeStamp),
    );

    let lastState = 0;

    for (const p of pausedSeries) {
      if (Date.parse(p.timeStamp) > timestamp) break;
      lastState = typeof p.value === 'number' ? p.value : parseFloat(String(p.value));
    }

    return lastState === 1;
  };

  const rawSeries = utils.getUniqueArrayItemsByProperty(chart.timeseries, 'timeStamp')
    .sort((a, b) => Date.parse(a.timeStamp) - Date.parse(b.timeStamp));

  const pauseTimeStamps = (paused?.timeseries || []).map(p => Date.parse(p.timeStamp));
  const allTimeStamps = Array.from(new Set([
    ...rawSeries.map(s => Date.parse(s.timeStamp)),
    ...pauseTimeStamps,
  ])).sort((a, b) => a - b);

  const seriesData: [number, number | null][] = [];

  allTimeStamps.forEach(ts => {
    const pausedNow = isPausedAt(ts);

    if (pausedNow) {
      seriesData.push([ts, null]);
    } else {
      const originalPoint = rawSeries.find(s => Date.parse(s.timeStamp) === ts);

      if (originalPoint) {
        const numericValue = parseFloat(originalPoint.value);
        const formatted = shouldConvertToWh
          ? formatEnergyValue(numericValue, chart.measurementUnit!.displayShort)
          : { value: numericValue };

        seriesData.push([ts, formatted.value]);
      }
    }
  });

  return {
    ...(noTitle
      ? {
        title: {
          text: '',
        },
      }
      : {
        title: {
          align: 'left',
          text: chart.friendlyName || chart.name,
        },
      }),
    ...{
      accessibility: {
        screenReaderSection: {
          beforeChartFormat: `
            <{headingTagName}>{chartTitle}</{headingTagName}>
            <div>{chartSubtitle}</div>
            <div>{chartLongdesc}</div>
            <div>{xAxisDescription}</div>
            <div>{yAxisDescription}</div>
          `,
        },
      },
      chart: {
        events: {
          render(this: Highcharts.Chart) {
            const legend = (this.legend as { group?: { element: SVGElement } });

            if (legend?.group?.element) {
              legend.group.element.style.pointerEvents = 'none';
            }
          },
        },
        spacing: chartSpacing,
      },
      exporting: {
        enabled: false,
      },
      lang: {
        noData: 'No data found for selected period',
      },
      legend: {
        enabled: true,
        itemHoverStyle: { cursor: 'default' },
        itemStyle: { cursor: 'default' },
        symbolRadius: 2,
      },
      plotOptions: {
        series: {
          connectNulls: false,
          getExtremesFromAll: true,
          states: {
            hover: {
              enabled: false,
            },
          },
        },
      },
      responsive: {
        rules: compactResponsiveRules,
      },
      series: [
        {
          color: '#009E80',
          data: seriesData,
          fillColor: {
            linearGradient: {
              x1: 0,
              x2: 0,
              y1: 0,
              y2: 1,
            },
            stops: [
              [0.1, 'rgba(0, 158, 128, 0.4)'],
              [1, 'rgba(0, 158, 128, 0)'],
            ],
          },
          lineWidth: 1,
          marker: {
            enabled: showDots,
            radius: 3,
          },
          name: `${chart.friendlyName || chart.name} (${displayUnitShort || ''})`,
          step: undefined,
          tooltip: {
            xDateFormat: '%A, %e %b, %H:%M:%S',
          },
          type: 'area',
        },
        ...(paused?.timeseries.length ? [{
          color: 'transparent',
          data: [] as [number, number | null][],
          fillColor: 'rgba(115, 119, 136, 0.3)',
          marker: { enabled: false },
          name: 'Paused',
          showInLegend: true,
          type: 'area' as const,
        }] : []),
      ],
      tooltip: {
        valueDecimals: 6,
      },
      xAxis: {
        crosshair: {
          color: 'rgba(138, 145, 242, 0.50)',
          width: 1,
        },
        type: 'datetime',
      },
      yAxis: {
        title: {
          text: displayUnitLong,
        },
      },
    },
  };
};

export const createPredefinedValuesChart = ({ chart, paused, showDots = true, noTitle = false }: CreateChartParams): Highcharts.Options => {
  const isPausedAt = (timestamp: number) => {
    if (!paused || !paused.timeseries.length) {
      return false;
    }

    const pausedSeries = [...paused.timeseries].sort((a, b) =>
      Date.parse(a.timeStamp) - Date.parse(b.timeStamp),
    );

    let lastState = 0;

    for (const p of pausedSeries) {
      if (Date.parse(p.timeStamp) > timestamp) {
        break;
      }
      lastState = typeof p.value === 'number' ? p.value : parseFloat(p.value);
    }

    return lastState === 1;
  };

  const rawSeries = utils.getUniqueArrayItemsByProperty(chart.timeseries, 'timeStamp')
    .sort((a, b) => Date.parse(a.timeStamp) - Date.parse(b.timeStamp));

  const values: number[] = [];

  const seriesData: [number, number | null][] = [];

  const pauseTimeStamps = (paused?.timeseries || []).map(p => Date.parse(p.timeStamp));
  const allTimeStamps = Array.from(new Set([
    ...rawSeries.map(s => Date.parse(s.timeStamp)),
    ...pauseTimeStamps,
  ])).sort((a, b) => a - b);

  allTimeStamps.forEach(ts => {
    const pausedNow = isPausedAt(ts);
    const lastOriginalPoint = [...rawSeries].reverse().find(s => Date.parse(s.timeStamp) <= ts);

    if (pausedNow) {
      seriesData.push([ts, null]);
    } else if (lastOriginalPoint) {
      const val = parseFloat(lastOriginalPoint.value);

      values.push(val);
      seriesData.push([ts, val]);
    }
  });

  const minValue = values.length ? Math.min(...values) : 0;
  const maxValue = values.length ? Math.max(...values) : 1;

  return {
    ...(noTitle
      ? {
        title: {
          text: '',
        },
      }
      : {
        title: {
          align: 'left',
          text: chart.friendlyName || chart.name,
        },
      }),
    ...{
      accessibility: {
        screenReaderSection: {
          beforeChartFormat: `
            <{headingTagName}>{chartTitle}</{headingTagName}>
            <div>{chartSubtitle}</div><div>{chartLongdesc}</div>
            <div>{xAxisDescription}</div>
            <div>{yAxisDescription}</div>
          `,
        },
      },
      chart: {
        events: {
          render(this: Highcharts.Chart) {
            const legend = (this.legend as unknown as { group?: { element: SVGElement } });

            if (legend?.group?.element) {
              legend.group.element.style.pointerEvents = 'none';
            }
          },
        },
        spacing: chartSpacing,
      },
      legend: {
        enabled: true,
        itemHoverStyle: { cursor: 'default' },
        itemStyle: { cursor: 'default' },
        symbolRadius: 1,
      },
      plotOptions: {
        area: {
          connectNulls: false,
        },
        series: {
          getExtremesFromAll: true,
          marker: {
            enabled: true,
          },
          states: {
            hover: {
              enabled: false,
            },
          },
        },
      },
      responsive: {
        rules: compactResponsiveRules,
      },
      series: [
        {
          color: '#009E80',
          data: seriesData,
          fillColor: {
            linearGradient: {
              x1: 0,
              x2: 0,
              y1: 0,
              y2: 1,
            },
            stops: [
              [0.1, 'rgba(0, 158, 128, 0.4)'],
              [1, 'rgba(0, 158, 128, 0)'],
            ],
          },
          lineWidth: 1,
          marker: {
            enabled: showDots,
            radius: 3,
          },
          name: `${chart.friendlyName || chart.name}`,
          step: 'left',
          tooltip: {
            xDateFormat: '%A, %e %b, %H:%M:%S',
          },
          type: 'area',
        },
        ...(paused?.timeseries.length ? [{
          color: 'rgba(8, 9, 13, 0.10)',
          data: [] as [number, number | null][],
          fillColor: 'rgba(8, 9, 13, 0.05)',
          lineWidth: 1,
          marker: { enabled: false },
          name: 'Paused',
          showInLegend: true,
          type: 'area' as const,
        }] : []),
      ],
      xAxis: {
        crosshair: {
          color: 'rgba(138, 145, 242, 0.50)',
          width: 1,
        },
        type: 'datetime',
      },
      yAxis: {
        allowDecimals: false,
        max: maxValue !== minValue ? maxValue : (maxValue + 1),
        title: {
          text: chart.measurementUnit?.display || '',
        },
      },
    },
  };
};

export const createMonthlyChart = (data: number[], year: number): Highcharts.Options => ({
  chart: {
    spacing: chartSpacing,
    type: 'column',
  },
  series: [
    {
      color: '#8A91F2',
      data,
      showInLegend: false,
      type: 'column',
    },
  ],
  subtitle: {
    text: '',
  },
  title: {
    align: 'left',
    text: `Incidents by Month (${year})`,
  },
  tooltip: {
    enabled: true,
    formatter() {
      return this.x + '<br/>Incidents: ' + '<b>' + this.y + '</b>';
    },
  },
  xAxis: {
    categories: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
    crosshair: false,
  },
  yAxis: {
    allowDecimals: false,
    min: 0,
    title: {
      text: 'Incidents',
    },
  },
  ...exportingConfig,
});

export const createMonthlyIncidentsSeverityChart = (incidents: IncidentDto[], selectedYear: number): Highcharts.Options => {
  const criticalData = new Array(12).fill(null);
  const majorData = new Array(12).fill(null);
  const minorData = new Array(12).fill(null);

  incidents.forEach(incident => {
    const incidentDate = new Date(incident.resolvedTimestamp || incident.createdTimestamp);

    if (incidentDate.getFullYear() === selectedYear) {
      const monthIndex = incidentDate.getMonth();

      switch (incident.severity) {
        case IncidentSeverity.CRITICAL:
          criticalData[monthIndex] = (criticalData[monthIndex] || 0) + 1;
          break;
        case IncidentSeverity.MAJOR:
          majorData[monthIndex] = (majorData[monthIndex] || 0) + 1;
          break;
        case IncidentSeverity.MINOR:
          minorData[monthIndex] = (minorData[monthIndex] || 0) + 1;
          break;
        default:
          criticalData[monthIndex] = (criticalData[monthIndex] || 0) + 1;
      }
    }
  });

  return {
    chart: {
      spacing: chartSpacing,
      type: 'column',
    },
    plotOptions: {
      column: {
        borderWidth: 0.5,
        dataLabels: {
          enabled: false,
        },
        minPointLength: 5,
        stacking: 'normal',
      },
    },
    series: [
      {
        color: '#FFE07A',
        data: minorData,
        name: 'Minor',
        type: 'column',
      },
      {
        color: '#E39200',
        data: majorData,
        name: 'Major',
        type: 'column',
      },
      {
        color: '#E0111C',
        data: criticalData,
        name: 'Critical',
        type: 'column',
      },
    ],
    title: {
      align: 'left',
      text: `Incidents by Month (${selectedYear})`,
    },
    tooltip: {
      headerFormat: '<b>{category}</b><br/>',
      pointFormat: '{series.name}: {point.y}<br/>Total: {point.stackTotal}',
    },
    xAxis: {
      categories: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
    },
    yAxis: {
      min: 0,
      stackLabels: {
        enabled: false,
      },
      title: {
        text: 'Incidents',
      },
    },
  };
};

const SeverityOpacity = {
  CRITICAL: 0.75,
  MAJOR: 0.5,
  MINOR: 0.25,
};

const processDonutChartData = (incidents: IncidentDto[]) => {
  const manufacturersMap: Record<string, { severity: Record<string, number>; total: number; }> = {};

  incidents.forEach((incident) => {
    const make = incident.device?.deviceModelInformation?.make?.name || 'Unknown';
    const severity = (incident.severity || 'critical');

    if (!manufacturersMap[make]) {
      manufacturersMap[make] = { severity: {}, total: 0 };
    }

    manufacturersMap[make].total++;
    manufacturersMap[make].severity[severity] = (manufacturersMap[make].severity[severity] || 0) + 1;
  });

  return {
    manufacturersMap,
    totalIncidents: incidents.length,
  };
};

export const createIncidentDonutChart = (incidents: IncidentDto[]): Highcharts.Options => {
  const { totalIncidents, manufacturersMap } = processDonutChartData(incidents);

  const manufacturerData: Highcharts.PointOptionsObject[] = [];
  const severityData: Highcharts.PointOptionsObject[] = [];

  const baseColors = Highcharts.getOptions().colors?.map(c => {
    return Highcharts.color(c);
  }) || [];

  Object.entries(manufacturersMap).forEach(([make, info], index) => {
    const parentColor = baseColors[index % baseColors.length];

    manufacturerData.push({
      color: parentColor.get(),
      name: make,
      y: info.total,
    });

    Object.entries(info.severity).forEach(([sevName, sevCount]) => {
      const opacity = SeverityOpacity[sevName.toUpperCase() as keyof typeof SeverityOpacity];

      severityData.push({
        color: parentColor.setOpacity(opacity).get(),
        custom: {
          parentName: make,
        },
        name: sevName.charAt(0).toUpperCase() + sevName.slice(1),
        y: sevCount,
      });
    });
  });

  return {
    chart: {
      type: 'pie',
    },

    plotOptions: {
      pie: {
        borderColor: '#ffffff',
        borderWidth: 1,
        center: ['50%', '50%'],
        shadow: false,
        states: {
          hover: {
            brightness: 0.1,
          },
          inactive: {
            opacity: 0.4,
          },
        },
        stickyTracking: false,
      },
    },
    series: [
      {
        data: [{ color: '#f8f9fa', name: 'Total', y: totalIncidents }],
        dataLabels: {
          distance: '-30%',
        },
        name: 'Total',
        size: '30%',
        type: 'pie',
      },
      {
        data: manufacturerData,
        dataLabels: {
          distance: '-35%',
          enabled: true,
          format: '{point.name}',
          style: { color: '#fff', fontSize: '10px', textOutline: '1px black' },
        },
        innerSize: '35%',
        name: 'Manufacturers',
        size: '60%',
        type: 'pie',
      },
      {
        data: severityData,
        dataLabels: {
          enabled: true,
          format: '{point.name}: {y}',
          overflow: 'allow',
          padding: 0,
          style: { fontSize: '10px' },
        },
        innerSize: '65%',
        name: 'Severity',
        size: '100%',
        type: 'pie',
      },
    ],
    title: {
      text: 'Open Incidents by Manufacturer',
    },
    tooltip: {
      formatter(this: Highcharts.Point) {
        if (this.series.name === 'Total') {
          return `Total: <b>${this.y}</b>`;
        }

        const custom = this.options.custom as
          | { parentName?: string; };

        const parent = custom?.['parentName'];

        if (parent) {
          return `
            <b>${parent}</b><br/>
            Severity: ${this.name}<br/>
            Count: <b>${this.y}</b>
          `;
        }

        return `
          <b>${this.name}</b><br/>
          Total: <b>${this.y}</b>
        `;
      },
      shared: false,
      useHTML: true,
    },
  };
};

export const createDeviceModelChart = (incidents: IncidentItemDto[] | null): Highcharts.Options => {
  const categoryHeight = 10;
  const categoryPadding = 5;
  const chartContentHeight = 120;
  const deviceModels: { count: number; id: string; manufacturerName: string; modelName: string; }[] = [];

  incidents?.forEach(incident => {
    const model = incident.device.deviceModel;

    if (!model) {
      return;
    }

    const deviceModel = deviceModels.find(dm => dm.id === model.id);

    if (deviceModel) {
      deviceModel.count += 1;
    } else {
      deviceModels.push({
        count: 1,
        id: model.id,
        manufacturerName: model.maker?.name ?? '',
        modelName: model.name ?? '',
      });
    }
  });
  const sortedDeviceModels = deviceModels.sort((a, b) => b.count - a.count);
  const categories = sortedDeviceModels.map(deviceModel => `${deviceModel.modelName} (${deviceModel.manufacturerName})`);
  const seriesData = sortedDeviceModels.map(deviceModel => deviceModel.count);

  return {
    chart: {
      height: chartContentHeight + deviceModels.length * (categoryHeight + 2 * categoryPadding),
      spacing: chartSpacing,
      type: 'bar',
    },
    legend: {
      align: 'right',
      enabled: true,
      layout: 'vertical',
      verticalAlign: 'middle',
    },
    series: [
      {
        color: '#8A91F2',
        data: seriesData,
        pointPadding: categoryPadding,
        pointWidth: categoryHeight as number,
        showInLegend: false,
        type: 'bar',
      },
    ],
    subtitle: {
      text: '',
    },
    title: {
      align: 'left',
      text: 'Incidents by Device Model',
    },
    tooltip: {
      enabled: true,
      formatter() {
        return this.x + '<br/>Incidents: ' + '<b>' + this.y + '</b>';
      },
    },
    xAxis: {
      categories,
      crosshair: false,
    },
    yAxis: {
      allowDecimals: false,
      min: 0,
      title: {
        text: 'Incidents',
      },
    },
    ...exportingConfig,
  };
};

interface HighchartsStackPoint extends Highcharts.Point {
  stackTotal?: number;
}

export const createIncidentSeverityByDeviceModelChart = (incidents: IncidentItemDto[] | null): Highcharts.Options => {
  const categoryHeight = 10;
  const categoryPadding = 5;
  const chartContentHeight = 120;
  const deviceModelsMap: Record<string, {
    critical: number;
    id: string;
    major: number;
    manufacturerName: string;
    minor: number;
    modelName: string;
    total: number;
  }> = {};

  incidents?.forEach(incident => {
    const modelInfo = incident.device.deviceModel;

    if (!modelInfo) return;

    const id = modelInfo.id;

    if (!deviceModelsMap[id]) {
      deviceModelsMap[id] = {
        critical: 0,
        id,
        major: 0,
        manufacturerName: modelInfo.maker?.name ?? '',
        minor: 0,
        modelName: modelInfo.name ?? '',
        total: 0,
      };
    }

    const severity = incident.severity;

    switch (severity) {
      case IncidentSeverity.MINOR:
        deviceModelsMap[id].minor++;
        break;
      case IncidentSeverity.MAJOR:
        deviceModelsMap[id].major++;
        break;
      case IncidentSeverity.CRITICAL:
      default:
        deviceModelsMap[id].critical++;
        break;
    }

    deviceModelsMap[id].total++;
  });

  const deviceModels = Object.values(deviceModelsMap);

  const sortedModels = deviceModels.sort((a, b) => b.total - a.total);
  const categories = sortedModels.map(m => `${m.modelName} (${m.manufacturerName})`);

  return {
    chart: {
      height: chartContentHeight + deviceModels.length * (categoryHeight + 2 * categoryPadding),
      spacing: [10, 10, 15, 10],
      type: 'bar',
    },
    legend: {
      enabled: false,
    },
    plotOptions: {
      bar: {
        borderWidth: 0.5,
        pointWidth: categoryHeight,
        stacking: 'normal',
        stickyTracking: false,
      },
    },
    series: [
      {
        color: '#E0111C',
        data: sortedModels.map(m => m.critical),
        name: 'Critical',
        type: 'bar',
      },
      {
        color: '#E39200',
        data: sortedModels.map(m => m.major),
        name: 'Major',
        type: 'bar',
      },
      {
        color: '#FFE07A',
        data: sortedModels.map(m => m.minor),
        name: 'Minor',
        type: 'bar',
      },
    ],
    title: {
      align: 'left',
      text: 'Incidents by Device Model',
    },
    tooltip: {

      formatter() {
        const point = this as HighchartsStackPoint;

        return `<b>${this.x}</b>
          <br/>${this.series.name}: <b>${this.y}</b>
          <br/>Total: <b>${point.stackTotal}</b>`;
      },
    },
    xAxis: {
      categories,
      crosshair: false,
    },
    yAxis: {
      allowDecimals: false,
      min: 0,
      stackLabels: {
        enabled: false,
      },
      title: {
        text: 'Incidents',
      },
    },
    ...exportingConfig,
  };
};

export const createIncidentManufacturerChart = (incidents: IncidentDto[]): Highcharts.Options => {
  const stats = incidents.reduce((acc, incident) => {
    const manufacturerName = incident.device?.deviceModelInformation?.make?.name || 'Unknown';

    acc[manufacturerName] = (acc[manufacturerName] || 0) + 1;

    return acc;
  }, {} as Record<string, number>);

  const data = Object.entries(stats).map(([name, count]) => ({
    name,
    y: count,
  }));

  return {
    chart: {
      plotShadow: false,
      spacing: chartSpacing,
      type: 'pie',
    },
    plotOptions: {
      pie: {
        allowPointSelect: true,
        cursor: 'pointer',
        dataLabels: {
          enabled: true,
          format: '{point.name}<br/> {point.y}',
          style: {
            color: 'black',
          },
        },
      },
    },
    series: [
      {
        data,
        name: 'Incidents',
        type: 'pie',
      },
    ],
    title: {
      align: 'left',
      margin: 50,
      text: 'Open Incidents by Manufacturer',
    },
    tooltip: {
      enabled: false,
    },
    ...exportingConfig,
  };
};

export const createGaugeChart = (config: WidgetGauge, chart: DeviceChart | null): Highcharts.Options => {
  const lastValue = chart?.timeseries?.at(-1)?.value;
  const roundedValue = lastValue != null ? Math.round(lastValue) : undefined;
  const pipe = new FormatNumberPipe();

  return {
    chart: {
      plotBackgroundColor: undefined,
      plotBackgroundImage: undefined,
      plotBorderWidth: 0,
      plotShadow: false,
      type: 'gauge',
    },

    exporting: {
      enabled: false,
    },

    noData: {
      position: {
        verticalAlign: 'bottom',
      },
    },

    pane: {
      background: undefined,
      center: ['50%', '58%'],
      endAngle: 90,
      startAngle: -90,
    },

    series: [{
      data: roundedValue ? [roundedValue != null && roundedValue > config.criticalMax
        ? config.criticalMax
        : roundedValue] : undefined,
      dataLabels: {
        borderWidth: 0,
        format: `
          <div class="text-center">
            <div class="text-2xl font-semibold text-ui-dark leading-6">
              ${pipe.transform(lastValue || 0, chart?.measurementUnit?.displayShort)}
            </div>
            <div class="text-sm font-semibold text-ui-gray-100">(Current)</div>
          </div>
        `,
        useHTML: true,
        y: 16,
      },
      dial: {
        backgroundColor: 'gray',
        baseLength: '0%',
        baseWidth: 8,
        radius: '85%',
        rearLength: '0%',
      },
      pivot: {
        backgroundColor: 'gray',
        radius: lastValue ? 4 : 0,
      },
      type: 'gauge',
    }],

    title: {
      text: '',
    },

    tooltip: {
      enabled: false,
    },

    yAxis: {
      labels: {
        distance: 15,
        style: {
          fontSize: '12px',
        },
      },
      lineWidth: 0,
      max: config.criticalMax,
      min: config.normalMin,
      minorTickInterval: undefined,
      plotBands: [{
        borderRadius: '3px',
        color: '#55BF3B',
        from: config.normalMin,
        thickness: 10,
        to: config.normalMax,
      }, {
        borderRadius: '3px',
        color: '#DDDF0D',
        from: config.warningMin,
        thickness: 10,
        to: config.warningMax,
      }, {
        borderRadius: '3px',
        color: '#DF5353',
        from: config.criticalMin,
        thickness: 10,
        to: config.criticalMax,
      }],
      tickColor: '#FFFFFF',
      tickLength: 20,
      tickPixelInterval: 72,
      tickPosition: 'inside',
      tickWidth: 2,
    },
  };
};

interface CustomPointOptions extends Highcharts.PointOptionsObject {
  date: number;
  originalValue: number;
}

interface CustomPoint extends Highcharts.Point {
  options: CustomPointOptions;
}

export const createConnectedChart = (chart: DeviceChart): Highcharts.Options => {
  const UPTIME_COLORS = {
    DOWN: '#737788',
    UP: '#38c0a1',
  };

  const sortedTimestamp: { timeStamp: string; value: string; }[] = utils.getUniqueArrayItemsByProperty(chart.timeseries, 'timeStamp')
    .sort((a, b) => new Date(a.timeStamp).getTime() - new Date(b.timeStamp).getTime());
  const rawData = sortedTimestamp.map(({ value }) => parseFloat(value));
  const categories = sortedTimestamp.map(({ timeStamp }) => timeStamp);
  const processedData: Highcharts.PointOptionsObject[] = rawData.map((originalValue, index) => {
    const isUp = originalValue === 1;

    return {
      color: isUp ? UPTIME_COLORS.UP : UPTIME_COLORS.DOWN,
      date: Date.parse(categories[index]),
      name: categories[index],
      originalValue,
      y: 1,
    };
  });

  return {
    chart: {
      spacing: chartSpacing,
      type: 'column',
    },
    series: [
      {
        borderRadius: 0,
        borderWidth: 0,
        data: processedData,
        groupPadding: 0,
        pointPadding: 0.1,
        showInLegend: false,
        type: 'column',
      },
    ],
    subtitle: {
      text: '',
    },
    title: {
      align: 'left',
      text: 'Connected',
    },

    tooltip: {
      formatter() {
        const point = this as (CustomPoint & { options: { originalValue: number; }; });

        if (!point) {
          return '';
        }

        const dateStr = Highcharts.dateFormat('%A, %e %b, %H:%M:%S', point.options.date);
        const originalValue = point.options.originalValue;
        const statusText = originalValue === 1 ? 'Connected' : 'Disconnected';
        const pointColor = typeof point.color === 'string' ? point.color : '';
        const statusRow = `
          <span style="color:${pointColor}; font-size:14px;">\u25CF</span>
          Status: <b>${statusText}</b>
        `;

        return `
          <span style="font-size: 10px">${dateStr}</span><br/>
          ${statusRow}
        `;
      },
      shared: true,
    },
    xAxis: {
      categories,
      crosshair: false,
      lineWidth: 0,
      tickWidth: 0,
      visible: false,
    },
    yAxis: {
      // max and min values help to adjust the height of the rendered column
      max: 2,
      min: -1,
      title: {
        text: '',
      },
      visible: false,
    },
    ...exportingConfig,
  };
};
